/**
 * Home Route — Figma-faithful home screen
 *
 * Filter tabs (serif underline) + vertical mission feed with rich cards.
 * No IkigaiMiniWidget, no ChatInput (not in Figma home).
 *
 * Data: Missions from LangGraph Store (persisted by TriggerContext).
 *       Shows empty state + onboarding when no missions exist yet.
 *
 * @see Figma_assets/iOS/Home@2x.png
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@ownyou/ui-design-system';
import type { Mission as UIMission, MissionCardType } from '@ownyou/ui-components';
import { EmptyState } from '@ownyou/ui-components';

import { MissionFeed } from '@/components/home/MissionFeed';
import { MissionFilterTabs } from '@/components/home/MissionFilterTabs';
import { HomeEmptyState } from '@/components/home/HomeEmptyState';
import { HomeLoadingState } from '@/components/home/HomeLoadingState';
import { PullToRefresh } from '@/components/shared/PullToRefresh';
import { NotificationBadge } from '@/components/shared/NotificationBadge';
import { useTrigger } from '../contexts/TriggerContext';
import { useDataSource } from '../contexts/DataSourceContext';
import { useMissions, type Mission as StoreMission } from '../hooks/useMissions';
import { getEffectiveRefreshConfig } from '../config/refresh-config';
import { useEngagementLogger } from '../hooks/useEngagementLogger';
import { useNotifications } from '../hooks/useNotifications';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import { recordEntityFeedback, type EntityFeedback } from '@ownyou/ikigai';
import { NS } from '@ownyou/shared-types';
import { formatTimeAgo } from '@/utils/format-time';
import { PredictionTracker } from '@/services/prediction-tracker';
import { recordPredictionOutcome } from '@/services/engagement-profile-bridge';
import { useAwardPoints } from '@/hooks/useRewards';
import {
  createMissionSpineRunId,
  recordMissionSpineMarker,
} from '@/lib/mission-spine/instrumentation';
import {
  legacySentimentToReaction,
  recordMissionReaction,
  type MissionReactionRecord,
} from '@/lib/missions/reactions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HomeState = 'empty' | 'loading' | 'ready' | 'error';
type HomeFeedbackSentiment = 'positive' | 'negative';

export interface HomeProps {
  initialState?: HomeState;
}

// ---------------------------------------------------------------------------
// Map Store mission type to UI MissionCardType
// ---------------------------------------------------------------------------

const TYPE_TO_CARD_TYPE: Record<string, MissionCardType> = {
  shopping: 'shopping',
  savings: 'savings',
  travel: 'travel',
  content: 'content',
  food: 'food',
  restaurant: 'food',
  entertainment: 'entertainment',
  events: 'entertainment',
  health: 'health',
  diagnostic: 'health',
  people: 'people',
  consumables: 'consumables',
};

function reactionToHomeSentiment(reaction: MissionReactionRecord['reaction']): HomeFeedbackSentiment | null {
  if (reaction === 'like') return 'positive';
  if (reaction === 'dislike') return 'negative';
  return null;
}

function storeMissionToUI(
  m: StoreMission,
): UIMission & {
  alignmentBoost?: number;
  alignedDimensions?: string[];
  isGrowthMission?: boolean;
  isStale?: boolean;
  state?: StoreMission['state'];
  status?: StoreMission['status'];
  snoozedUntil?: number;
} {
  // Sprint 17e: Check if card is stale (older than its category refresh interval)
  const createdAtMs = m.createdAt instanceof Date ? m.createdAt.getTime() : (m.createdAt as unknown as number);
  const refreshInterval = getEffectiveRefreshConfig().agentRefreshInterval[m.type];
  const age = Date.now() - createdAtMs;
  const isStale = refreshInterval ? age > refreshInterval : false;

  return {
    id: m.id,
    type: TYPE_TO_CARD_TYPE[m.type] ?? 'content',
    title: m.title,
    subtitle: m.description || m.reason || '',
    imageUrl: m.imageUrl,
    brandName: m.brandName,
    brandLogoUrl: m.brandLogoUrl,
    price: m.price,
    originalPrice: m.originalPrice,
    currency: m.currency,
    createdAt: createdAtMs,
    alignmentBoost: m.alignmentBoost,
    alignedDimensions: m.alignedDimensions,
    isGrowthMission: m.isGrowthMission,
    isStale,
    state: m.state,
    status: m.status,
    snoozedUntil: m.snoozedUntil,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Home({ initialState }: HomeProps = {}) {
  const navigate = useNavigate();
  const { isExecuting, refreshPipeline } = useTrigger();
  const { dataSources } = useDataSource();
  const {
    missions: storeMissions,
    allMissions: rawAllStoreMissions,
    isLoading: missionsLoading,
    hasMissions,
    refreshMissions,
    lastRefreshAt,
  } = useMissions();
  const allStoreMissions = rawAllStoreMissions ?? storeMissions ?? [];
  const hasConnectedSources = dataSources.some(ds => ds.status === 'connected');
  const engagementLogger = useEngagementLogger();
  const { unreadCount, markAllRead } = useNotifications();
  const { store, isReady: storeReady } = useStore();
  const { wallet, isAuthenticated, isLoading: authLoading } = useAuth();
  const feedbackUserId = wallet?.address ?? 'anonymous';
  const { awardBonus } = useAwardPoints();

  // PredictionTracker for recording hit/miss from feedback (memoized for write-lock stability)
  const predictionTracker = useMemo(() => {
    if (!store || !storeReady) return null;
    return new PredictionTracker(store, feedbackUserId);
  }, [store, storeReady, feedbackUserId]);

  // Map all Store missions to UI format. Lifecycle chips decide visibility.
  const missions: UIMission[] = useMemo(() => {
    return allStoreMissions.map(storeMissionToUI);
  }, [allStoreMissions]);

  // Determine view state from Store + TriggerContext
  const [state, setState] = useState<HomeState>(initialState ?? 'ready');

  useEffect(() => {
    if (initialState) {
      setState(initialState);
    } else if (!authLoading && !isAuthenticated) {
      // Session expired or not logged in — show empty state with login prompt
      setState('empty');
    } else if (missionsLoading || authLoading || (isExecuting && !hasMissions)) {
      setState('loading');
    } else if (!hasMissions) {
      setState('empty');
    } else {
      setState('ready');
    }
  }, [initialState, isExecuting, missionsLoading, hasMissions, isAuthenticated, authLoading]);

  const [filter, setFilter] = useState('new');
  const [expandedMissions, setExpandedMissions] = useState<Set<string>>(new Set());

  const handleTryMe = useCallback((missionId: string) => {
    setExpandedMissions(prev => new Set(prev).add(missionId));
    // Record as a 'hit' — user accepted the prediction
    const mission = allStoreMissions.find(m => m.id === missionId);
    if (predictionTracker && mission) {
      const raw = mission as unknown as Record<string, unknown>;
      recordPredictionOutcome(predictionTracker, { type: (raw?.type as string) ?? 'unknown' }, 'accepted').catch(console.warn);
    }
  }, [allStoreMissions, predictionTracker]);

  const handleNoWay = useCallback((missionId: string) => {
    setExpandedMissions(prev => new Set(prev).add(missionId));
    // Record as a 'miss' — user rejected the prediction
    const mission = allStoreMissions.find(m => m.id === missionId);
    if (predictionTracker && mission) {
      const raw = mission as unknown as Record<string, unknown>;
      recordPredictionOutcome(predictionTracker, { type: (raw?.type as string) ?? 'unknown' }, 'dismissed').catch(console.warn);
    }
  }, [allStoreMissions, predictionTracker]);
  const [feedbackStates, setFeedbackStates] = useState<Map<string, HomeFeedbackSentiment>>(new Map());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasRestoredFeedbackRef = useRef(false);

  // Restore persisted feedback states from Store (once)
  useEffect(() => {
    if (!storeMissions.length || hasRestoredFeedbackRef.current || !store || !storeReady) return;

    const restoreFeedback = async () => {
      const restored = new Map<string, HomeFeedbackSentiment>();
      try {
        const result = await store.list<MissionReactionRecord>(NS.missionFeedback(feedbackUserId), { limit: 1000, offset: 0 });
        for (const item of result.items) {
          const sentiment = reactionToHomeSentiment(item.reaction);
          if (item.missionId && sentiment) restored.set(item.missionId, sentiment);
        }
      } catch (error) {
        console.warn('[Home] Failed to restore canonical mission reactions:', error);
      }

      for (const m of storeMissions) {
        if (restored.has(m.id)) continue;
        const raw = m as unknown as Record<string, unknown>;
        const saved = raw.feedbackSentiment as HomeFeedbackSentiment | undefined;
        if (saved) restored.set(m.id, saved);
      }

      hasRestoredFeedbackRef.current = true;
      if (restored.size > 0) {
        setFeedbackStates(restored);
      }
    };

    restoreFeedback();
  }, [feedbackUserId, store, storeMissions, storeReady]);

  // Card-first lifecycle filtering is handled by useMissions; Home must not
  // re-filter against the legacy lifecycle namespace cache.
  const filteredMissions = useMemo(() => {
    console.log(`[Home] Card-first visible missions: ${missions.length}`);
    return missions;
  }, [missions]);

  useEffect(() => {
    if (state !== 'ready') return;
    recordMissionSpineMarker({
      runId: createMissionSpineRunId('today-visible'),
      hop: 'today_visible_missions',
      status: filteredMissions.length > 0 ? 'success' : 'skipped',
      reasonCode: filteredMissions.length > 0 ? 'today-missions-visible' : 'today-no-visible-missions',
      counts: {
        storeMissionCount: missions.length,
        visibleMissionCount: filteredMissions.length,
      },
    });
  }, [state, missions.length, filteredMissions.length]);

  const handleFilterChange = useCallback((newFilter: string) => {
    setFilter(newFilter);
  }, []);

  const handleRetry = useCallback(async () => {
    setState('loading');
    try {
      refreshMissions();
      await refreshPipeline();
    } finally {
      setState('ready');
    }
  }, [refreshMissions, refreshPipeline]);

  const handleRefresh = useCallback(async () => {
    refreshMissions();
    await new Promise((r) => setTimeout(r, 400));
  }, [refreshMissions]);

  const triggerFullRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshPipeline();
      refreshMissions();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshPipeline, refreshMissions]);

  const handleFeedback = useCallback((missionId: string, sentiment: HomeFeedbackSentiment) => {
    const currentSentiment = feedbackStates.get(missionId);
    const nextSentiment = currentSentiment === sentiment ? null : sentiment;
    const reaction = nextSentiment ? legacySentimentToReaction(nextSentiment) : 'neutral';

    // Toggle visual state
    setFeedbackStates((prev) => {
      const next = new Map(prev);
      if (nextSentiment === null) {
        next.delete(missionId);
      } else {
        next.set(missionId, nextSentiment);
      }
      return next;
    });

    // Find the mission to get entity context
    const mission = allStoreMissions.find(m => m.id === missionId);
    const raw = mission as unknown as Record<string, unknown> | undefined;

    // Log engagement event
    if (nextSentiment) {
      const action = nextSentiment === 'positive' ? 'emoji_positive' as const : 'emoji_negative' as const;
      engagementLogger?.log({
        missionId,
        action,
        agentType: (raw?.type as string) ?? 'unknown',
        entitySource: raw?.entitySource as string | undefined,
        entityCategory: (raw?.entityCategory ?? raw?.type) as string | undefined,
        isComposite: false,
        isCascade: false,
        source: 'detection',
      }).catch(console.warn);
    }

    // Record entity feedback (tap for positive, dispute for negative)
    const entitySource = raw?.entitySource as string | undefined;
    if (nextSentiment && entitySource && store) {
      const feedbackType: EntityFeedback['type'] = nextSentiment === 'positive' ? 'tap' : 'dispute';
      recordEntityFeedback(feedbackUserId, {
        entityId: entitySource,
        runId: 'emoji',
        type: feedbackType,
        timestamp: Date.now(),
        metadata: { missionId },
      }, store as Parameters<typeof recordEntityFeedback>[2]).catch(console.warn);
    }

    // Record prediction outcome for accuracy tracking
    if (nextSentiment && predictionTracker && mission) {
      const action = nextSentiment === 'positive' ? 'emoji_positive' as const : 'emoji_negative' as const;
      recordPredictionOutcome(
        predictionTracker,
        { type: (raw?.type as string) ?? 'unknown' },
        action,
        (threshold, tokens) => {
          awardBonus('threshold_crossed', `${(raw?.type as string) ?? 'Unknown'} accuracy hit ${threshold}%`, tokens, { threshold, category: (raw?.type as string) ?? 'unknown' }).catch(() => {});
        },
      ).catch(console.warn);
    }

    // Persist canonical mission reaction plus legacy card projection.
    if (store) {
      recordMissionReaction({
        store,
        userId: feedbackUserId,
        missionId,
        reaction,
        source: 'card',
      }).then(() => {
        refreshMissions();
      }).catch(console.warn);

      if (raw) {
        const projection = { ...raw };
        if (nextSentiment) {
          projection.feedbackSentiment = nextSentiment;
        } else {
          delete projection.feedbackSentiment;
        }
        store.put(NS.missionCards(feedbackUserId), missionId, projection).catch(console.warn);
      }
    }
  }, [allStoreMissions, engagementLogger, store, feedbackUserId, predictionTracker, refreshMissions, awardBonus, feedbackStates]);

  // ----- Render states -----

  if (state === 'empty') {
    return (
      <div data-testid="home-screen">
        <HomeEmptyState onConnect={() => navigate('/welcome')} hasConnectedSources={hasConnectedSources} />
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div data-testid="home-screen">
        <HomeLoadingState />
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div data-testid="home-screen" className="flex items-center justify-center min-h-[60vh] px-4">
        <EmptyState
          illustration="error"
          headline="Something went wrong"
          description="We couldn't load your missions. Please try again."
          action={{ label: 'Retry', onClick: handleRetry }}
        />
      </div>
    );
  }

  // state === 'ready'
  return (
    <div data-testid="home-screen" className={cn('flex flex-col')}>
      <PullToRefresh onRefresh={handleRefresh}>
        {/* Filter tabs — serif underline style */}
        <MissionFilterTabs
          activeFilter={filter}
          onFilterChange={handleFilterChange}
          className="mb-4"
        />

        {/* Sprint 17e: Refresh status header + Sprint 17g-3: Notification badge */}
        <div className="flex items-center justify-between px-4 mb-3" data-testid="refresh-header">
          <span className="text-xs text-slate-400" data-testid="refresh-timestamp">
            {formatTimeAgo(lastRefreshAt)}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="today-chat-trigger"
              onClick={() => window.dispatchEvent(new CustomEvent('ownyou:open-chat'))}
              className="text-xs font-medium px-3 py-1 rounded-full transition-colors"
              style={{
                background: 'var(--aurora-ink)',
                color: 'var(--aurora-card-solid)',
              }}
            >
              Ask
            </button>
            <button
              onClick={markAllRead}
              className="relative"
              data-testid="notification-trigger"
            >
              <NotificationBadge count={unreadCount} />
            </button>
            <button
              onClick={triggerFullRefresh}
              disabled={isRefreshing}
              data-testid="refresh-button"
              className={cn(
                'text-xs font-medium px-3 py-1 rounded-full transition-colors',
                isRefreshing
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 active:bg-indigo-200',
              )}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh missions'}
            </button>
          </div>
        </div>

        {/* Scrollable mission feed — rich type-specific cards */}
        <MissionFeed
          missions={filteredMissions}
          filter={filter}
          onMissionView={(m) => navigate(`/mission/${m.id}`)}
          onMissionFeedback={handleFeedback}
          feedbackStates={feedbackStates}
          expandedMissions={expandedMissions}
          onTryMe={handleTryMe}
          onNoWay={handleNoWay}
        />
      </PullToRefresh>
    </div>
  );
}

export default Home;
